import * as React from 'react';
import { getContrastColorTo, isDefined, isEmpty, type HexAlpha } from '@nfctron/nexus-core';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { type VariantProps } from 'tailwind-variants';

import { type ExtendedComponentProps } from '../types';
import { cn, isHexAlpha, isThemeColor, tv, type ThemeColor } from '../utils';
import { UiTooltip, UiTooltipContent, UiTooltipTrigger } from './tooltip';

/* -------------------------------------------------------------------------------------------------
 * Badge
 * -----------------------------------------------------------------------------------------------*/

const badgeVariants = tv({
	base: [
		'inline-flex items-center border font-semibold transition-colors truncate',
		'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
		'[&>.leftSection]:shrink-0 [&>.rightSection]:shrink-0',
	],
	variants: {
		variant: {
			filled: 'border-transparent text-white',
			subtle: 'border-transparent',
			outlined: 'bg-transparent',
			unstyled: '',
		},
		color: {
			primary: '',
			unstyled: '',
			/** theme colors */
			white: '',
			black: '',
			slate: '',
			gray: '',
			zinc: '',
			neutral: '',
			stone: '',
			red: '',
			orange: '',
			amber: '',
			yellow: '',
			lime: '',
			green: '',
			emerald: '',
			teal: '',
			cyan: '',
			sky: '',
			blue: '',
			indigo: '',
			violet: '',
			purple: '',
			fuchsia: '',
			pink: '',
			rose: '',
			'purple-heart': '',
			'lucky-point': '',
		},
		rounded: {
			full: 'rounded-full',
			default: 'rounded-md',
			unstyled: '',
		},
		size: {
			sm: ['text-2xs leading-[12px] px-1.5 py-0.5 gap-1', '[&>.leftSection]:size-2', '[&>.rightSection]:size-2'],
			default: ['text-xs px-2 py-0 gap-2', '[&>.leftSection]:size-4', '[&>.rightSection]:size-4'],
			lg: ['text-sm px-3 py-1 gap-2', '[&>.leftSection]:size-5', '[&>.rightSection]:size-5'],
			unstyled: '',
		},
	},
	compoundVariants: [
		/** primary */
		{
			variant: 'filled',
			color: 'primary',
			className: cn('bg-lucky-point-500', 'dark:bg-lucky-point-600'),
		},
		{
			variant: 'subtle',
			color: 'primary',
			className: cn('bg-lucky-point-25 text-lucky-point-500', 'dark:bg-lucky-point-900/50 dark:text-lucky-point-200'),
		},
		{
			variant: 'outlined',
			color: 'primary',
			className: cn('text-lucky-point-500 border-lucky-point-50', 'dark:text-lucky-point-300 dark:border-lucky-point-700'),
		},
		/** white */
		{
			variant: 'filled',
			color: 'white',
			className: cn('bg-white text-zinc-900', 'dark:bg-white/90'),
		},
		{
			variant: 'subtle',
			color: 'white',
			className: cn('bg-white/75 text-zinc-700', 'dark:bg-white/20 dark:text-zinc-200'),
		},
		{
			variant: 'outlined',
			color: 'white',
			className: cn('border-white/25', 'dark:text-white/90 dark:border-white/30'),
		},
		/** black */
		{
			variant: 'filled',
			color: 'black',
			className: cn('bg-black text-white', 'dark:bg-black/90'),
		},
		{
			variant: 'subtle',
			color: 'black',
			className: cn('bg-black/75 text-white', 'dark:bg-black/50'),
		},
		{
			variant: 'outlined',
			color: 'black',
			className: cn('border-black/25', 'dark:text-zinc-200 dark:border-black/50'),
		},
		/** slate */
		{
			variant: 'filled',
			color: 'slate',
			className: cn('bg-slate-500', 'dark:bg-slate-700'),
		},
		{
			variant: 'subtle',
			color: 'slate',
			className: cn('bg-slate-50 text-slate-700', 'dark:bg-slate-800/50 dark:text-slate-200'),
		},
		{
			variant: 'outlined',
			color: 'slate',
			className: cn('text-slate-700 border-slate-100', 'dark:text-slate-300 dark:border-slate-700'),
		},
		/** gray */
		{
			variant: 'filled',
			color: 'gray',
			className: cn('bg-gray-500', 'dark:bg-gray-700'),
		},
		{
			variant: 'subtle',
			color: 'gray',
			className: cn('bg-gray-50 text-gray-700', 'dark:bg-gray-800/50 dark:text-gray-200'),
		},
		{
			variant: 'outlined',
			color: 'gray',
			className: cn('text-gray-700 border-gray-100', 'dark:text-gray-300 dark:border-gray-700'),
		},
		/** zinc */
		{
			variant: 'filled',
			color: 'zinc',
			className: cn('bg-zinc-500', 'dark:bg-zinc-700'),
		},
		{
			variant: 'subtle',
			color: 'zinc',
			className: cn('bg-zinc-50 text-zinc-700', 'dark:bg-zinc-800/50 dark:text-zinc-200'),
		},
		{
			variant: 'outlined',
			color: 'zinc',
			className: cn('text-zinc-700 border-zinc-100', 'dark:text-zinc-300 dark:border-zinc-700'),
		},
		/** neutral */
		{
			variant: 'filled',
			color: 'neutral',
			className: cn('bg-neutral-500', 'dark:bg-neutral-700'),
		},
		{
			variant: 'subtle',
			color: 'neutral',
			className: cn('bg-neutral-50 text-neutral-700', 'dark:bg-neutral-800/50 dark:text-neutral-200'),
		},
		{
			variant: 'outlined',
			color: 'neutral',
			className: cn('text-neutral-700 border-neutral-100', 'dark:text-neutral-300 dark:border-neutral-700'),
		},
		/** stone */
		{
			variant: 'filled',
			color: 'stone',
			className: cn('bg-stone-500', 'dark:bg-stone-700'),
		},
		{
			variant: 'subtle',
			color: 'stone',
			className: cn('bg-stone-50 text-stone-700', 'dark:bg-stone-800/50 dark:text-stone-200'),
		},
		{
			variant: 'outlined',
			color: 'stone',
			className: cn('text-stone-700 border-stone-100', 'dark:text-stone-300 dark:border-stone-700'),
		},
		/** red */
		{
			variant: 'filled',
			color: 'red',
			className: cn('bg-red-500', 'dark:bg-red-900'),
		},
		{
			variant: 'subtle',
			color: 'red',
			className: cn('bg-red-50 text-red-700', 'dark:bg-red-950/50 dark:text-red-200'),
		},
		{
			variant: 'outlined',
			color: 'red',
			className: cn('text-red-700 border-red-100', 'dark:text-red-300 dark:border-red-700'),
		},
		/** orange */
		{
			variant: 'filled',
			color: 'orange',
			className: cn('bg-orange-500', 'dark:bg-orange-900'),
		},
		{
			variant: 'subtle',
			color: 'orange',
			className: cn('bg-orange-50 text-orange-700', 'dark:bg-orange-950/50 dark:text-orange-200'),
		},
		{
			variant: 'outlined',
			color: 'orange',
			className: cn('text-orange-700 border-orange-100', 'dark:text-orange-300 dark:border-orange-700'),
		},
		/** amber */
		{
			variant: 'filled',
			color: 'amber',
			className: cn('bg-amber-500', 'dark:bg-amber-900'),
		},
		{
			variant: 'subtle',
			color: 'amber',
			className: cn('bg-amber-50 text-amber-700', 'dark:bg-amber-950/50 dark:text-amber-200'),
		},
		{
			variant: 'outlined',
			color: 'amber',
			className: cn('text-amber-700 border-amber-100', 'dark:text-amber-300 dark:border-amber-700'),
		},
		/** yellow */
		{
			variant: 'filled',
			color: 'yellow',
			className: cn('bg-yellow-500', 'dark:bg-yellow-900'),
		},
		{
			variant: 'subtle',
			color: 'yellow',
			className: cn('bg-yellow-50 text-yellow-700', 'dark:bg-yellow-950/50 dark:text-yellow-200'),
		},
		{
			variant: 'outlined',
			color: 'yellow',
			className: cn('text-yellow-700 border-yellow-100', 'dark:text-yellow-300 dark:border-yellow-700'),
		},
		/** lime */
		{
			variant: 'filled',
			color: 'lime',
			className: cn('bg-lime-500', 'dark:bg-lime-900'),
		},
		{
			variant: 'subtle',
			color: 'lime',
			className: cn('bg-lime-50 text-lime-700', 'dark:bg-lime-950/50 dark:text-lime-200'),
		},
		{
			variant: 'outlined',
			color: 'lime',
			className: cn('text-lime-700 border-lime-100', 'dark:text-lime-300 dark:border-lime-700'),
		},
		/** green */
		{
			variant: 'filled',
			color: 'green',
			className: cn('bg-green-500', 'dark:bg-green-900'),
		},
		{
			variant: 'subtle',
			color: 'green',
			className: cn('bg-green-50 text-green-700', 'dark:bg-green-950/50 dark:text-green-200'),
		},
		{
			variant: 'outlined',
			color: 'green',
			className: cn('text-green-700 border-green-100', 'dark:text-green-300 dark:border-green-700'),
		},
		/** emerald */
		{
			variant: 'filled',
			color: 'emerald',
			className: cn('bg-emerald-500', 'dark:bg-emerald-900'),
		},
		{
			variant: 'subtle',
			color: 'emerald',
			className: cn('bg-emerald-50 text-emerald-700', 'dark:bg-emerald-950/50 dark:text-emerald-200'),
		},
		{
			variant: 'outlined',
			color: 'emerald',
			className: cn('text-emerald-700 border-emerald-100', 'dark:text-emerald-300 dark:border-emerald-700'),
		},
		/** teal */
		{
			variant: 'filled',
			color: 'teal',
			className: cn('bg-teal-500', 'dark:bg-teal-900'),
		},
		{
			variant: 'subtle',
			color: 'teal',
			className: cn('bg-teal-50 text-teal-700', 'dark:bg-teal-950/50 dark:text-teal-200'),
		},
		{
			variant: 'outlined',
			color: 'teal',
			className: cn('text-teal-700 border-teal-100', 'dark:text-teal-300 dark:border-teal-700'),
		},
		/** cyan */
		{
			variant: 'filled',
			color: 'cyan',
			className: cn('bg-cyan-500', 'dark:bg-cyan-900'),
		},
		{
			variant: 'subtle',
			color: 'cyan',
			className: cn('bg-cyan-50 text-cyan-700', 'dark:bg-cyan-950/50 dark:text-cyan-200'),
		},
		{
			variant: 'outlined',
			color: 'cyan',
			className: cn('text-cyan-700 border-cyan-100', 'dark:text-cyan-300 dark:border-cyan-700'),
		},
		/** sky */
		{
			variant: 'filled',
			color: 'sky',
			className: cn('bg-sky-500', 'dark:bg-sky-900'),
		},
		{
			variant: 'subtle',
			color: 'sky',
			className: cn('bg-sky-50 text-sky-700', 'dark:bg-sky-950/50 dark:text-sky-200'),
		},
		{
			variant: 'outlined',
			color: 'sky',
			className: cn('text-sky-700 border-sky-100', 'dark:text-sky-300 dark:border-sky-700'),
		},
		/** blue */
		{
			variant: 'filled',
			color: 'blue',
			className: cn('bg-blue-500', 'dark:bg-blue-900'),
		},
		{
			variant: 'subtle',
			color: 'blue',
			className: cn('bg-blue-50 text-blue-700', 'dark:bg-blue-950/50 dark:text-blue-200'),
		},
		{
			variant: 'outlined',
			color: 'blue',
			className: cn('text-blue-700 border-blue-100', 'dark:text-blue-300 dark:border-blue-700'),
		},
		/** indigo */
		{
			variant: 'filled',
			color: 'indigo',
			className: cn('bg-indigo-500', 'dark:bg-indigo-900'),
		},
		{
			variant: 'subtle',
			color: 'indigo',
			className: cn('bg-indigo-50 text-indigo-700', 'dark:bg-indigo-950/50 dark:text-indigo-200'),
		},
		{
			variant: 'outlined',
			color: 'indigo',
			className: cn('text-indigo-700 border-indigo-100', 'dark:text-indigo-300 dark:border-indigo-700'),
		},
		/** violet */
		{
			variant: 'filled',
			color: 'violet',
			className: cn('bg-violet-500', 'dark:bg-violet-900'),
		},
		{
			variant: 'subtle',
			color: 'violet',
			className: cn('bg-violet-50 text-violet-700', 'dark:bg-violet-950/50 dark:text-violet-200'),
		},
		{
			variant: 'outlined',
			color: 'violet',
			className: cn('text-violet-700 border-violet-100', 'dark:text-violet-300 dark:border-violet-700'),
		},
		/** purple */
		{
			variant: 'filled',
			color: 'purple',
			className: cn('bg-purple-500', 'dark:bg-purple-900'),
		},
		{
			variant: 'subtle',
			color: 'purple',
			className: cn('bg-purple-50 text-purple-700', 'dark:bg-purple-950/50 dark:text-purple-200'),
		},
		{
			variant: 'outlined',
			color: 'purple',
			className: cn('text-purple-700 border-purple-100', 'dark:text-purple-300 dark:border-purple-700'),
		},
		/** fuchsia */
		{
			variant: 'filled',
			color: 'fuchsia',
			className: cn('bg-fuchsia-500', 'dark:bg-fuchsia-900'),
		},
		{
			variant: 'subtle',
			color: 'fuchsia',
			className: cn('bg-fuchsia-50 text-fuchsia-700', 'dark:bg-fuchsia-950/50 dark:text-fuchsia-200'),
		},
		{
			variant: 'outlined',
			color: 'fuchsia',
			className: cn('text-fuchsia-700 border-fuchsia-100', 'dark:text-fuchsia-300 dark:border-fuchsia-700'),
		},
		/** pink */
		{
			variant: 'filled',
			color: 'pink',
			className: cn('bg-pink-500', 'dark:bg-pink-900'),
		},
		{
			variant: 'subtle',
			color: 'pink',
			className: cn('bg-pink-50 text-pink-700', 'dark:bg-pink-950/50 dark:text-pink-200'),
		},
		{
			variant: 'outlined',
			color: 'pink',
			className: cn('text-pink-700 border-pink-100', 'dark:text-pink-300 dark:border-pink-700'),
		},
		/** rose */
		{
			variant: 'filled',
			color: 'rose',
			className: cn('bg-rose-500', 'dark:bg-rose-900'),
		},
		{
			variant: 'subtle',
			color: 'rose',
			className: cn('bg-rose-50 text-rose-700', 'dark:bg-rose-950/50 dark:text-rose-200'),
		},
		{
			variant: 'outlined',
			color: 'rose',
			className: cn('text-rose-700 border-rose-100', 'dark:text-rose-300 dark:border-rose-700'),
		},
		/** purple-heart */
		{
			variant: 'filled',
			color: 'purple-heart',
			className: cn('bg-purple-heart-500', 'dark:bg-purple-heart-700'),
		},
		{
			variant: 'subtle',
			color: 'purple-heart',
			className: cn('bg-purple-heart-50 text-purple-heart-700', 'dark:bg-purple-heart-950/50 dark:text-purple-heart-200'),
		},
		{
			variant: 'outlined',
			color: 'purple-heart',
			className: cn('text-purple-heart-700 border-purple-heart-100', 'dark:text-purple-heart-300 dark:border-purple-heart-700'),
		},
		/** lucky-point */
		{
			variant: 'filled',
			color: 'lucky-point',
			className: cn('bg-lucky-point-500', 'dark:bg-lucky-point-700'),
		},
		{
			variant: 'subtle',
			color: 'lucky-point',
			className: cn('bg-lucky-point-25 text-lucky-point-500', 'dark:bg-lucky-point-950/50 dark:text-lucky-point-200'),
		},
		{
			variant: 'outlined',
			color: 'lucky-point',
			className: cn('text-lucky-point-500 border-lucky-point-50', 'dark:text-lucky-point-300 dark:border-lucky-point-700'),
		},
	],
	defaultVariants: {
		variant: 'filled',
		color: 'primary',
		size: 'default',
		rounded: 'default',
	},
});

export interface UiBadgeProps extends Omit<ExtendedComponentProps<VariantProps<typeof badgeVariants>, 'div'>, 'color'> {
	leftSection?: React.ReactNode;
	rightSection?: React.ReactNode;
	color?: ThemeColor | HexAlpha;
	tooltip?: React.ReactNode;
}

const UiBadge = React.forwardRef<HTMLDivElement, UiBadgeProps>(function UiBadge(
	{ className, style, variant, size, color, rounded, children, leftSection, rightSection, tooltip, ...props },
	ref,
) {
	const themeColor = isThemeColor(color) ? color : 'unstyled';
	const badgeVariantsClassNames = badgeVariants({ variant, size, rounded, color: themeColor });

	const badgeNode = (
		<div
			className={cn(badgeVariantsClassNames, className)}
			style={{
				backgroundColor: isHexAlpha(color) ? color : undefined,
				color: isHexAlpha(color) ? getContrastColorTo(color) : undefined,
				...style,
			}}
			{...props}
			ref={ref}
		>
			{/* left section */}
			{isDefined(leftSection) && <Slot className="leftSection shrink-0">{leftSection}</Slot>}
			<Slottable>
				{/* children */}
				{children}
			</Slottable>
			{/* right section */}
			{isDefined(rightSection) && <Slot className="rightSection shrink-0">{rightSection}</Slot>}
		</div>
	);

	if (!isEmpty(tooltip)) {
		return (
			<UiTooltip>
				<UiTooltipTrigger asChild>{badgeNode}</UiTooltipTrigger>
				<UiTooltipContent>{tooltip}</UiTooltipContent>
			</UiTooltip>
		);
	}

	return badgeNode;
});

/* -----------------------------------------------------------------------------------------------*/

export { UiBadge, badgeVariants };
